// Keep the proctoring capture <video> element alive across browsers.
//
// The proctoring loop grabs frames with `ctx.drawImage(videoEl, …)`. If that
// <video> is created with document.createElement and never inserted into the
// DOM (or hidden with display:none), WebKit/Safari — and backgrounded tabs on
// every engine — stop advancing its frames, so drawImage silently re-copies the
// last painted frame. That produced byte-identical "frozen" proctoring images
// in production (and on iOS the camera track is released on background).
//
// Fix, validated on the standalone test page (incl. iPhone/iOS Safari):
//  1. mount the capture <video> IN the DOM but visually hidden (NOT display:none,
//     NOT detached) so frames keep flowing;
//  2. watch the camera track and, when it ends/mutes (iOS background), clear the
//     refs so the next capture tick re-acquires a fresh stream.

// Mount the offscreen capture element into the DOM, rendered but invisible.
export const mountHiddenProctorVideo = videoEl => {
  if (!videoEl) {
    return;
  }
  // Remove any previously-mounted proctor video so re-acquire/teardown paths
  // never leave stale hidden elements accumulating in the DOM.
  try {
    const prior = document.querySelectorAll('video[data-pl-proctor]');
    for (let i = 0; i < prior.length; i++) {
      if (prior[i] !== videoEl && prior[i].parentNode) {
        prior[i].parentNode.removeChild(prior[i]);
      }
    }
  } catch (e) {
    // ignore
  }
  videoEl.setAttribute('data-pl-proctor', '1');
  videoEl.muted = true;
  videoEl.setAttribute('playsinline', '');
  videoEl.setAttribute('autoplay', '');
  // Tiny + near-transparent + on-screen: kept in the render tree so the browser
  // keeps decoding frames, but invisible to the candidate.
  videoEl.style.position = 'fixed';
  videoEl.style.right = '0';
  videoEl.style.bottom = '0';
  videoEl.style.width = '2px';
  videoEl.style.height = '2px';
  videoEl.style.opacity = '0.01';
  videoEl.style.pointerEvents = 'none';
  videoEl.style.zIndex = '-1';
  if (!videoEl.isConnected) {
    document.body.appendChild(videoEl);
  }
};

// Remove the hidden capture element from the DOM (call on teardown).
export const unmountProctorVideo = videoEl => {
  try {
    if (videoEl && videoEl.parentNode) {
      videoEl.parentNode.removeChild(videoEl);
    }
  } catch (e) {
    // ignore
  }
};

// When the camera track ends/mutes (common on iOS when the page backgrounds),
// run `onLost` so the caller can drop the dead refs and let the next capture
// tick re-acquire a fresh stream.
export const watchProctorTrack = (stream, onLost) => {
  const track =
    stream && typeof stream.getVideoTracks === 'function'
      ? stream.getVideoTracks()[0]
      : null;
  if (!track) {
    return;
  }
  track.onended = () => {
    if (typeof onLost === 'function') {
      onLost('track-ended');
    }
  };
  track.onmute = () => {
    if (typeof onLost === 'function') {
      onLost('track-muted');
    }
  };
};
